import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';
import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import { map } from 'rxjs/operators';
import TopPage from 'v2/components/Shared/TopPage';
import bankDetailsService from 'v2/services/bankDetails';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import fidcStatusService from 'v2/services/fidic';
import { financialStatementService } from 'v2/services/financialStatement/statement';

import type { IBankAccount } from 'v2/interfaces/financial';

import { AppRedirectModal } from './AppRedirectModal';
import { BankDetailsTable, MIN_BALANCE_TO_CREATE_ACCOUNT } from './BankDetailsTable';
import { NewAccountWarningModal } from './NewAccountWarningModal';
import { useStyles } from './styles';

function BankDetails() {
  const classes = useStyles();
  const [isOpenNewAccountWarningModal, setIsOpenNewAccountWarningModal] = React.useState(false);
  const [isOpenAppRedirectModal, setIsOpenAppRedirectModal] = React.useState(false);
  const [canCreateNewAccountBank, setCanCreateNewAccountBank] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [bankDetails, setBankDetails] = React.useState<IBankAccount[]>(null);

  const [isFidcAuthorized] = useObservable(() => fidcStatusService.checkFidcApi(user?.id), [user?.id]);

  const [currentBalance] = useObservable(
    () =>
      financialStatementService.getTotalAvailable().pipe(
        map((data) => {
          return data.data.total;
        })
      ),
    []
  );

  const toggleOpenNewAccountWarningModal = () => {
    setIsOpenNewAccountWarningModal((isOpen) => !isOpen);
  };

  const toggleOpenAppRedirectModal = () => {
    setIsOpenAppRedirectModal((isOpen) => !isOpen);
  };

  const handleCreateAccount = () => {
    if (user?.create_new_account_bank) {
      toggleOpenAppRedirectModal();
    } else {
      toggleOpenNewAccountWarningModal();
    }
  };

  const loadBankDetails = async () => {
    try {
      await bankDetailsService.listBankDetails().then((details) => setBankDetails(details));
    } catch (error) {
      toast.error(error);
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    loadBankDetails();
  }, []);

  React.useEffect(() => {
    if (bankDetails?.find((status) => status?.status_slug === 'active')) {
      return setCanCreateNewAccountBank(true);
    }

    if (isFidcAuthorized?.isAuthorized && currentBalance > MIN_BALANCE_TO_CREATE_ACCOUNT) {
      return setCanCreateNewAccountBank(true);
    }

    return setCanCreateNewAccountBank(false);
  }, [bankDetails, currentBalance, isFidcAuthorized?.isAuthorized]);

  const NewBankAccountButton = (
    <Button variant='outlined' id='new-bank-account' onClick={handleCreateAccount} disabled={!canCreateNewAccountBank}>
      Cadastrar nova conta
    </Button>
  );

  return (
    <Fade in timeout={500}>
      <div className={classes.root}>
        <style>
          {`#main-content {
            max-width: 1900px;
            margin: 0 auto;
          }`}
        </style>
        <TopPage title='Contas bancárias' additionalContent={NewBankAccountButton} />
        <BankDetailsTable
          businessName={user?.business_name}
          currentBalance={currentBalance}
          isFidcAuthorized={isFidcAuthorized?.isAuthorized}
          bankDetails={bankDetails}
          isLoading={isLoading}
          loadBankDetails={loadBankDetails}
        />
        <NewAccountWarningModal isOpen={isOpenNewAccountWarningModal} setClose={toggleOpenNewAccountWarningModal} />
        <AppRedirectModal isOpen={isOpenAppRedirectModal} setClose={toggleOpenAppRedirectModal} />
      </div>
    </Fade>
  );
}

export { BankDetails };
